/**
 * OpenAI Service - Servicio para integración con GPT API
 * Procesa texto de documentos bancarios y extrae información financiera
 */

import OpenAI from 'openai';
import type { Ingreso, Egreso } from '../types';
import { LoggerService } from './logger.service';

interface TransactionDataItem {
  descripcion: string;
  fecha: string;
  monto: number;
  moneda: 'ARS' | 'USD';
  cantidad: number;
  tipo: 'ingreso' | 'egreso';
  categoria: string;
}

interface ProcessedFinancialDocument {
  totalizador: {
    totalGastosPesos: number;
    totalGastosDolares: number;
    totalIngresosPesos: number;
    totalIngresosDolares: number;
    transaccionesTotales: number;
  };
  data: TransactionDataItem[];
  resumen: {
    fechaInicio: string;
    fechaFin: string;
    bancoOrigen?: string;
  };
}

// Tipo para datos de transacción legacy
interface TransactionData {
  concepto: string;
  monto: number;
  categoria: string;
  fecha: string;
  descripcion?: string;
}

// Interfaz de compatibilidad para el formato anterior
interface ProcessedBankDocument {
  ingresos: TransactionData[];
  egresos: TransactionData[];
  resumen: {
    totalIngresos: number;
    totalEgresos: number;
    transaccionesTotales: number;
    fechaInicio: string;
    fechaFin: string;
  };
}

export class OpenAIService {
  private client: OpenAI | null = null;
  private readonly model: string;

  constructor() {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    this.model = import.meta.env.VITE_OPENAI_MODEL || 'gpt-4o-mini';

    if (!apiKey || apiKey === 'tu_api_key_aqui') {
      LoggerService.warn('OpenAI API key not configured', 'OpenAIService');
      return;
    }

    try {
      this.client = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true // Solo para desarrollo - en producción usar backend
      });
      LoggerService.info('OpenAI client initialized', 'OpenAIService', { model: this.model });
    } catch (error) {
      LoggerService.error('Failed to initialize OpenAI client', 'OpenAIService', { error });
    }
  }

  /**
   * Verifica si el servicio está configurado correctamente
   */
  isConfigured(): boolean {
    return this.client !== null;
  }

  /**
   * Procesa texto de documento bancario y extrae transacciones
   */
  async processBankDocument(documentText: string): Promise<ProcessedBankDocument> {
    if (!this.client) {
      throw new Error('OpenAI client not configured');
    }

    // Validar que el documento contiene contenido bancario real
    const hasExpectedContent = documentText.includes('Franco Damian Sosa') || 
                              documentText.includes('BBVA') ||
                              documentText.includes('PEDIDOSYA') ||
                              documentText.includes('RAPPI') ||
                              documentText.includes('ADIDAS');
    
    if (!hasExpectedContent && documentText.length < 100) {
      throw new Error('El documento no parece contener transacciones bancarias válidas. Verifica que el PDF se procesó correctamente.');
    }

    LoggerService.info('Processing bank document with OpenAI', 'OpenAIService', {
      textLength: documentText.length,
      model: this.model,
      documentPreview: documentText.substring(0, 300) + '...',
      hasExpectedContent
    });

    try {
      const prompt = this.createBankDocumentPrompt(documentText);
      
      const completion = await this.client.chat.completions.create({
        model: this.model,
        messages: [
          {
            role: 'system',
            content: 'Eres un experto analista financiero que procesa documentos bancarios y extrae información de transacciones. Responde SOLO con JSON válido, sin texto adicional.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_completion_tokens: 8000 // Aumentado para modelos con reasoning
      });

      const responseText = completion.choices[0]?.message?.content;
      const finishReason = completion.choices[0]?.finish_reason;
      
      if (!responseText || responseText.trim() === '') {
        LoggerService.error('Empty response from OpenAI', 'OpenAIService', {
          finishReason,
          usage: completion.usage,
          model: this.model
        });
        
        if (finishReason === 'length') {
          throw new Error('La respuesta de OpenAI se cortó por límite de tokens. Intenta con un documento más corto o contacta soporte.');
        } else {
          throw new Error(`Respuesta vacía de OpenAI. Razón de finalización: ${finishReason}`);
        }
      }

      const processedData = this.parseGPTResponse(responseText);
      
      LoggerService.logUserAction('bank_document_processed', 'OpenAIService', {
        ingresos: processedData.ingresos.length,
        egresos: processedData.egresos.length,
        totalTransacciones: processedData.resumen.transaccionesTotales
      });

      return processedData;

    } catch (error: unknown) {
      LoggerService.error('Error processing bank document', 'OpenAIService', { error });
      
      // Mejorar el manejo de errores específicos de OpenAI
      if (error && typeof error === 'object' && 'status' in error) {
        const openAIError = error as { status: number; message?: string };
        switch (openAIError.status) {
          case 401:
            throw new Error('API Key de OpenAI inválida o expirada. Verifica tu configuración.');
          case 429:
            throw new Error('Límite de rate de OpenAI excedido. Intenta nuevamente en unos minutos.');
          case 403:
            throw new Error('Acceso denegado a OpenAI. Verifica tu plan y permisos.');
          case 500:
            throw new Error('Error interno de OpenAI. Intenta nuevamente más tarde.');
          default:
            throw new Error(`Error ${openAIError.status}: ${openAIError.message || 'Error desconocido de OpenAI'}`);
        }
      }
      
      if (error instanceof Error) {
        if (error.message.includes('fetch')) {
          throw new Error('Error de conectividad. Verifica tu conexión a internet.');
        }
        
        if (error.message.includes('quota')) {
          throw new Error('Cuota de OpenAI agotada. Verifica tu plan y facturación.');
        }
      }
      
      throw error;
    }
  }

  /**
   * Crea el prompt para procesar documentos bancarios - Optimizado para bancos argentinos
   */
  private createBankDocumentPrompt(documentText: string): string {
    return `
ANALIZA EXCLUSIVAMENTE EL SIGUIENTE DOCUMENTO BANCARIO REAL Y EXTRAE SUS TRANSACCIONES:

=== INICIO DEL DOCUMENTO ===
${documentText}
=== FIN DEL DOCUMENTO ===

INSTRUCCIONES CRÍTICAS - LEE ESTO ATENTAMENTE:
- SI NO VES TRANSACCIONES REALES EN EL DOCUMENTO ARRIBA, RESPONDE CON ERROR
- DEBES procesar ÚNICAMENTE las transacciones que aparecen literalmente en el documento
- BUSCA en el texto nombres como: ADIDAS, PEDIDOSYA, RAPPI, AUTOPISTAS, NETFLIX, MERCADOPAGO
- BUSCA fechas como: 23Ago25, 28Ago25, 31Ago25, etc.
- BUSCA montos como: 39.999,66, 41.784,00, etc.
- Si NO encuentras estas referencias específicas, el documento no se procesó correctamente
- NO generes datos ficticios como "Sueldo", "Supermercado", "Freelance" que no están en el documento
- El documento debe contener "Franco Damian Sosa" y transacciones de BBVA

El JSON debe incluir:

1. Un totalizador general con el total de gastos en pesos y en dólares
2. Una sección "data" que sea un array de objetos, donde cada objeto represente una transacción individual

FORMATO DE RESPUESTA REQUERIDO (JSON):
{
  "totalizador": {
    "totalGastosPesos": número,
    "totalGastosDolares": número,
    "totalIngresosPesos": número,
    "totalIngresosDolares": número,
    "transaccionesTotales": número
  },
  "data": [
    {
      "descripcion": "descripción de la transacción",
      "fecha": "YYYY-MM-DD",
      "monto": número,
      "moneda": "ARS" | "USD",
      "cantidad": número,
      "tipo": "ingreso" | "egreso",
      "categoria": "categoría apropiada"
    }
  ],
  "resumen": {
    "fechaInicio": "YYYY-MM-DD",
    "fechaFin": "YYYY-MM-DD",
    "bancoOrigen": "nombre del banco si se puede identificar"
  }
}

NOTAS IMPORTANTES:
- Usa "ARS" para pesos argentinos y "USD" para dólares
- Si no se especifica moneda, asume ARS
- Para categorías usa: Salario, Freelance, Ventas, Inversiones (ingresos) | Vivienda, Alimentación, Transporte, Entretenimiento, Servicios, Compras (egresos)
- La cantidad puede ser 1 si no se especifica otra cosa
- Extrae las fechas exactas del documento (formato DD-MMM-AA o similar)

VALIDACIÓN OBLIGATORIA ANTES DE RESPONDER:
- Si el documento contiene "ADIDAS ABASTO", "PEDIDOSYA", "RAPPI", incluye esas transacciones EXACTAS
- Si el documento contiene fechas "23Ago25", "28Ago25", usa esas fechas EXACTAS convertidas
- Si el documento contiene montos "39.999,66", "41.784,00", usa esos montos EXACTOS
- Si NO encuentras estos elementos específicos, responde: {"error": "No se encontraron transacciones reales en el documento"}

SOLO SI ENCUENTRAS TRANSACCIONES REALES, RESPONDE CON EL JSON COMPLETO.
NO GENERES DATOS FICTICIOS BAJO NINGUNA CIRCUNSTANCIA.
    `.trim();
  }

  /**
   * Parsea la respuesta de GPT y valida el formato - Actualizado para nuevo formato argentino
   */
  private parseGPTResponse(responseText: string): ProcessedBankDocument {
    try {
      // Limpiar la respuesta de posibles caracteres extra
      const cleanedResponse = responseText
        .replace(/```json/g, '')
        .replace(/```/g, '')
        .trim();

      const parsed = JSON.parse(cleanedResponse);
      
      // Verificar si OpenAI respondió con error por no encontrar datos reales
      if (parsed.error) {
        throw new Error(`OpenAI no pudo procesar el documento: ${parsed.error}. Verifica que el PDF contiene transacciones bancarias legibles.`);
      }
      
      // Validar estructura básica
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Respuesta no es un objeto JSON válido');
      }

      // Intentar primero el nuevo formato argentino
      if (parsed.data && Array.isArray(parsed.data) && parsed.totalizador) {
        return this.convertNewFormatToLegacy(parsed);
      }

      // Fallback al formato anterior
      return this.validateLegacyFormat(parsed);

    } catch (error) {
      LoggerService.error('Error parsing GPT response', 'OpenAIService', { 
        error, 
        responseText: responseText.substring(0, 500) 
      });
      
      // Crear una respuesta por defecto en caso de error
      return {
        ingresos: [],
        egresos: [],
        resumen: {
          totalIngresos: 0,
          totalEgresos: 0,
          transaccionesTotales: 0,
          fechaInicio: new Date().toISOString().split('T')[0],
          fechaFin: new Date().toISOString().split('T')[0]
        }
      };
    }
  }

  /**
   * Convierte el nuevo formato argentino al formato legacy para compatibilidad
   */
  private convertNewFormatToLegacy(newFormat: ProcessedFinancialDocument): ProcessedBankDocument {
    const ingresos = newFormat.data
      .filter((item: TransactionDataItem) => item.tipo === 'ingreso')
      .map((item: TransactionDataItem) => ({
        concepto: item.descripcion || 'Ingreso sin descripción',
        monto: Number(item.monto) || 0,
        categoria: item.categoria || 'Sin categoría',
        fecha: this.validateDate(item.fecha),
        descripcion: `${item.moneda || 'ARS'} - Cantidad: ${item.cantidad || 1}`
      }));

    const egresos = newFormat.data
      .filter((item: TransactionDataItem) => item.tipo === 'egreso')
      .map((item: TransactionDataItem) => ({
        concepto: item.descripcion || 'Gasto sin descripción',
        monto: Number(item.monto) || 0,
        categoria: item.categoria || 'Sin categoría',
        fecha: this.validateDate(item.fecha),
        descripcion: `${item.moneda || 'ARS'} - Cantidad: ${item.cantidad || 1}`
      }));

    const totalizador = newFormat.totalizador || {};
    const resumen = newFormat.resumen || {};

    return {
      ingresos,
      egresos,
      resumen: {
        totalIngresos: (Number(totalizador.totalIngresosPesos) || 0) + (Number(totalizador.totalIngresosDolares) || 0),
        totalEgresos: (Number(totalizador.totalGastosPesos) || 0) + (Number(totalizador.totalGastosDolares) || 0),
        transaccionesTotales: Number(totalizador.transaccionesTotales) || (ingresos.length + egresos.length),
        fechaInicio: resumen.fechaInicio || new Date().toISOString().split('T')[0],
        fechaFin: resumen.fechaFin || new Date().toISOString().split('T')[0]
      }
    };
  }

  /**
   * Valida y procesa el formato legacy
   */
  private validateLegacyFormat(parsed: Record<string, unknown>): ProcessedBankDocument {
    // Validar estructura
    if (!parsed.ingresos || !parsed.egresos || !parsed.resumen) {
      throw new Error('Invalid response structure from GPT');
    }

    // Validar y limpiar datos
    const ingresos = Array.isArray(parsed.ingresos) ? parsed.ingresos : [];
    const egresos = Array.isArray(parsed.egresos) ? parsed.egresos : [];
    const resumen = (parsed.resumen && typeof parsed.resumen === 'object' && parsed.resumen !== null) 
      ? parsed.resumen as Record<string, unknown> 
      : {};

    return {
      ingresos: this.validateTransactions(ingresos),
      egresos: this.validateTransactions(egresos),
      resumen: {
        totalIngresos: Number(resumen.totalIngresos) || 0,
        totalEgresos: Number(resumen.totalEgresos) || 0,
        transaccionesTotales: Number(resumen.transaccionesTotales) || 0,
        fechaInicio: String(resumen.fechaInicio || ''),
        fechaFin: String(resumen.fechaFin || '')
      }
    };
  }

  /**
   * Valida y normaliza los datos de transacciones
   */
  private validateTransactions(transactions: unknown[]): TransactionData[] {
    if (!Array.isArray(transactions)) {
      return [];
    }

    return transactions
      .filter(t => t && typeof t === 'object')
      .map(t => {
        const transaction = t as Record<string, unknown>;
        return {
          concepto: String(transaction.concepto || 'Transacción sin descripción'),
          monto: Number(transaction.monto) || 0,
          categoria: String(transaction.categoria || 'Sin categoría'),
          fecha: this.validateDate(String(transaction.fecha || '')),
          descripcion: transaction.descripcion ? String(transaction.descripcion) : undefined
        };
      })
      .filter(t => t.monto > 0); // Filtrar transacciones sin monto
  }

  /**
   * Valida y normaliza fechas
   */
  private validateDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // Si la fecha no es válida, usar fecha actual
        return new Date().toISOString().split('T')[0];
      }
      return date.toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  }

  /**
   * Convierte TransactionData a formato Ingreso
   */
  convertToIngreso(transaction: TransactionData): Omit<Ingreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'> {
    return {
      concepto: transaction.concepto,
      monto: transaction.monto,
      categoria: transaction.categoria,
      fecha: new Date(transaction.fecha),
      descripcion: transaction.descripcion || transaction.concepto
    };
  }

  /**
   * Convierte TransactionData a formato Egreso
   */
  convertToEgreso(transaction: TransactionData): Omit<Egreso, 'id' | 'userId' | 'createdAt' | 'updatedAt'> {
    return {
      concepto: transaction.concepto,
      monto: transaction.monto,
      categoria: transaction.categoria,
      fecha: new Date(transaction.fecha),
      descripcion: transaction.descripcion || transaction.concepto
    };
  }
}